import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivismOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  name: PropTypes.string,
  description: PropTypes.string,
  goalAmount: PropTypes.number,
  raisedAmount: PropTypes.number,
  status: PropTypes.string,
  departmentName: PropTypes.string,
  ministryName: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const NameCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.description || "No description"}
    </Typography>
  </Box>
);
NameCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
};

const ScopeCell = ({ row }) => {
  const { departmentName, ministryName } = row.original;
  if (departmentName) {
    return <Chip size="small" color="info" label={`Dept: ${departmentName}`} />;
  }
  if (ministryName) {
    return <Chip size="small" color="warning" label={`Ministry: ${ministryName}`} />;
  }
  return <Chip size="small" variant="outlined" label="General (church-wide)" />;
};
ScopeCell.propTypes = { row: PropTypes.shape({ original: rowPropTypes }).isRequired };

const GoalCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    {value ? money(value) : "No goal set"}
  </Typography>
);
GoalCell.propTypes = { value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };
GoalCell.defaultProps = { value: 0 };

const RaisedCell = ({ row }) => {
  const { goalAmount, raisedAmount } = row.original;
  const pct = goalAmount > 0 ? Math.min(100, Math.round((raisedAmount / goalAmount) * 100)) : 0;
  return (
    <Box minWidth={140}>
      <Typography variant="button" fontWeight={600} display="block">
        {money(raisedAmount)}
      </Typography>
      {goalAmount > 0 && (
        <>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            {pct}% of goal
          </Typography>
        </>
      )}
    </Box>
  );
};
RaisedCell.propTypes = { row: PropTypes.shape({ original: rowPropTypes }).isRequired };

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleContribute, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="success"
      startIcon={<VolunteerActivismIcon fontSize="small" />}
      onClick={() => handleContribute(row.original)}
    >
      Contribute
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleContribute: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useProjectData() {
  const { church, user } = useAuth();
  const isGroupHead = ["department_head", "ministry_head"].includes(user?.role);

  const [projectData, setProjectData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Project", accessor: "name", align: "left", width: "20%", Cell: NameCell },
      { Header: "Scope", accessor: "scope", align: "center", width: "15%", Cell: ScopeCell },
      { Header: "Goal", accessor: "goalAmount", align: "center", width: "15%", Cell: GoalCell },
      {
        Header: "Raised",
        accessor: "raisedAmount",
        align: "center",
        width: "20%",
        Cell: RaisedCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleContribute={handleContribute}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // -- Contribution modal state -----------------------------------------
  const [contributeModalOpen, setContributeModalOpen] = useState(false);
  const [contributingProject, setContributingProject] = useState(null);
  const [contribution, setContribution] = useState({ member_id: "", amount: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [isSavingContribution, setIsSavingContribution] = useState(false);
  const [isGroupContribution, setIsGroupContribution] = useState(false);
  const [contributorName, setContributorName] = useState("");

  const handleEdit = (project) => {
    setSelectedProject(project);
    setEditModalOpen(true);
  };

  const handleDelete = (project) => {
    setSelectedProject(project);
    setDeleteModalOpen(true);
  };

  const handleAddProject = () => {
    setSelectedProject({
      id: null,
      name: "",
      description: "",
      goalAmount: "",
      startDate: "",
      endDate: "",
      status: "active",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => window.print();

  const fetchProjects = useCallback(async () => {
    try {
      setProjectData((prev) => ({ ...prev, isLoading: true }));
      if (!church?.id) return;
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/projects${isGroupHead ? "/mine" : ""}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const projects = result.data || result || [];

      setProjectData((prev) => ({
        ...prev,
        rows: (Array.isArray(projects) ? projects : []).map((project, index) => ({
          id: project.id,
          serial: index + 1,
          name: project.name,
          description: project.description,
          goalAmount: Number(project.goal_amount || 0),
          raisedAmount: Number(project.contributions_sum_amount || 0),
          status: project.status === "active" ? "active" : project.status || "inactive",
          startDate: project.start_date,
          endDate: project.end_date,
          departmentName: project.department?.name || null,
          ministryName: project.ministry?.name || null,
          originalData: project,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch projects failed:", error);
      setProjectData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [church?.id, isGroupHead]);

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      if (!selectedProject?.name) throw new Error("Project name is required");

      const url = selectedProject.id
        ? `${API_BASE}/projects/${selectedProject.id}`
        : `${API_BASE}/projects`;
      const method = selectedProject.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedProject.name,
          description: selectedProject.description || null,
          goal_amount: selectedProject.goalAmount || null,
          start_date: selectedProject.startDate || null,
          end_date: selectedProject.endDate || null,
          status: selectedProject.status || "active",
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({ title: "Success!", text: "Project saved", icon: "success" });
      fetchProjects();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Project save failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/projects/${selectedProject.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({ title: "Success!", text: "Project deleted", icon: "success" });
      fetchProjects();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  // -- Contribution flow --------------------------------------------------

  const handleContribute = (project) => {
    setContributingProject(project);
    setContribution({ member_id: "", amount: "" });
    setSearchTerm("");
    setFilteredMembers([]);
    setIsGroupContribution(false);
    setContributorName("");
    setContributeModalOpen(true);
  };

  const searchMembers = useCallback(
    async (query) => {
      if (!church?.id || !query.trim()) {
        setFilteredMembers([]);
        return;
      }
      setIsLoadingMembers(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const response = await fetch(
          `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
          { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();
        setFilteredMembers(result.data || []);
      } catch (error) {
        console.error("Member search failed:", error);
        setFilteredMembers([]);
      } finally {
        setIsLoadingMembers(false);
      }
    },
    [church?.id]
  );

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }
    const timer = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchMembers]);

  const handleMemberSelect = (member) => {
    setContribution((prev) => ({ ...prev, member_id: member.id, member }));
    setShowMemberDropdown(false);
    setSearchTerm(`${member.first_name} ${member.surname} (${member.memberID})`);
  };

  const handleContributionSubmit = async () => {
    try {
      if (isGroupContribution) {
        if (!contributorName.trim()) {
          throw new Error(
            "Describe who/what this group contribution is for (e.g. Sunday service collection)."
          );
        }
      } else if (!contribution.member_id) {
        throw new Error("Pick a member to record this contribution for.");
      }
      if (!contribution.amount || Number(contribution.amount) <= 0) {
        throw new Error("Enter a contribution amount greater than zero.");
      }

      setIsSavingContribution(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: isGroupContribution ? null : contribution.member_id,
          contributor_name: isGroupContribution ? contributorName.trim() : null,
          type: "contribution",
          amount: contribution.amount,
          method: "manual",
          project_id: contributingProject.id,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({ title: "Success!", text: "Contribution recorded", icon: "success" });
      fetchProjects();
      setContributeModalOpen(false);
    } catch (error) {
      console.error("Contribution failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSavingContribution(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    ...projectData,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedProject,
    setSelectedProject,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddProject,
    // contribution flow
    contributeModalOpen,
    setContributeModalOpen,
    contributingProject,
    contribution,
    setContribution,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    isLoadingMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    handleContributionSubmit,
    isSavingContribution,
    isGroupContribution,
    setIsGroupContribution,
    contributorName,
    setContributorName,
  };
}
